import React from 'react';
import { PureComponent } from '../../../../dispatcher/modules';
import {
  MultiSelect,
  MultiSelectBlurEvent,
  TagData,
  ListItemProps
} from '@progress/kendo-react-dropdowns';
import _ from 'lodash';
import classNames from 'classnames';
import { Label } from '@progress/kendo-react-labels';
import Field from '../../../layout/field/Field';
import {
  PUZZLE_MULTISELECT_PROPS,
  PUZZLE_MULTISELECT_STATE
} from './Types';
import {
  ABSTRACT_FIELD_OMITTED_PROPS,
  FIELD_OMITTED_PROPS
} from '../../../../utility';
import { ChipProps } from '@progress/kendo-react-buttons';
import {
  MultiSelectCloseEvent,
  MultiSelectOpenEvent
} from '@progress/kendo-react-dropdowns/dist/npm/MultiSelect/MultiSelectProps';
import { Skeleton } from '@progress/kendo-react-indicators';

/**
 * For the layout and design purposes the entire Kendo Multiselect needs to be wrapped with this cmp.
 * Cmp handles all events that occur and flags component state represented with CSS classes
 * @author Stevan Stojanovic
 */
class PuzzleMultiselect extends PureComponent<PUZZLE_MULTISELECT_PROPS, PUZZLE_MULTISELECT_STATE> {
  ariaOwnsId: any;
  multiselectRef: any;
  multiselectID: any = null;

  static defaultProps = {
    size: 'medium'
  };

  state:PUZZLE_MULTISELECT_STATE = {
    isLastPage: false,
    isOpen: false
  }

  /**
   * Adding aria-required attribute to input - accessibility
   */
  componentDidMount() {
    const { required, id } = this.props;

    const element = this.multiselectRef._element;
    const tagsHolder = element?.querySelector('div.k-chip-list');
    if (tagsHolder) {
      tagsHolder.setAttribute('aria-label', this.translate('selectedItems'));
    }

    if (required) this.multiselectRef.base.component._input.setAttribute('aria-required', 'true');
    this.multiselectID = id ? id : this.multiselectRef?.base?.component?._input?.id;
    const input = this.multiselectRef._input;

    this.ariaOwnsId = input?.getAttribute('aria-owns');
    input?.removeAttribute('aria-owns');
    if (required) {
      input?.setAttribute('aria-required', 'true');
    }

    input?.setAttribute('role', 'combobox');
  }

  componentDidUpdate() {
    const { isLastPage, isOpen } = this.state;
    const { disabled } = this.props;

    if (disabled && isOpen) {
      const input = this.multiselectRef._input;
      input?.removeAttribute('aria-owns');
      this.setState({ isOpen: false });
    }

    if (isLastPage) {
      // if is the last page in the virtual scroll, then the component will be rerendered
      this.setState({ isLastPage: false });
    }
  }

  /**
   * Clear text from field and call onBlur method
   * @param {MultiSelectBlurEvent} e
   */
  private handleBlur = (e:MultiSelectBlurEvent):void => {
    const { onBlur, filterable } = this.props;

    if (filterable) {
      this.multiselectRef.setState?.({ text: '' });
    }

    if (onBlur) {
      onBlur(e);
    }
  }

  /**
   * Remove aria-owns attribute when dropdown is closed
   */
  onClose = (event: MultiSelectCloseEvent) => {
    const { onClose } = this.props;
    if (_.isFunction(onClose)) {
      onClose(event);
    }

    const input = this.multiselectRef._input;
    input?.removeAttribute('aria-owns');
    this.setState({ isOpen: false });
  };

  /**
   * Adds aria-owns attribute when dropdown is open
   */
  onOpen = (event: MultiSelectOpenEvent) => {
    const { onOpen } = this.props;
    if (_.isFunction(onOpen)) {
      onOpen(event);
    }

    const input = this.multiselectRef._input;
    input?.setAttribute('aria-owns', this.ariaOwnsId);

    const virtualScroll = this.multiselectRef?.base?.vs;
    if (virtualScroll) {
      const { pageSize, skip, total } = virtualScroll;
      if (skip + pageSize === total) {
        this.setState({ isLastPage: true });
      }
    }
    this.setState({ isOpen: true });
  };

  /**
   * Added to focus input element of MultiSelect on tag remove
   */
  onTagDelete = (data: any, evt: any) => {
    this.multiselectRef._input.focus();
    this.multiselectRef.onTagDelete(data, evt);
  };

  /**
   * Custom tag render method which will help CSS devs style different tag variants
   * @param tagData
   * @param li
   * */
  customTagRender = (
    tagData: TagData,
    li: React.ReactElement<any>
  ) => {
    let className = `${li.props.classname} tp-${tagData.data[0].themeColor}-tag`;
    let customTagProps: ChipProps = {
      ...li.props,
      className
    };
    let customTagText = (
      <React.Fragment
        key={li.props.id}
      >{li.props.text}</React.Fragment>
    );
    return React.cloneElement(li, customTagProps, [customTagText]);
  }

  /**
   * Item render for combobox with correct id name which will reflect id from the drop down(a11y mannner)
   * aria-activedescendant refers to the same id from the dropdown but without 'option-' string
   */
  private customComboboxItemRender = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
    let {
      dataItem
    } = itemProps;

    let text = dataItem;
    if (dataItem && this.props.textField) {
      text = dataItem[this.props.textField];
    }
    const modifiedProps = {
      ...li.props,
      id: itemProps.id?.replace('option-', '')
    };
    if (!text && this.props.skeletonEnabled) {
      text = (
        <Skeleton
          shape={'text'}
          style={{ width: '100%' }}
          animation={{ type: 'wave' }}
        />);
    } else {
      text = (
        <span
          className={'k-list-item-text ' + (dataItem && dataItem.itemDisabled ? 'k-disabled tp-pointer-events-none' : '')}
          key={'data-1'}
          title={text}
        >{text}</span>
      );
    }

    // add span with classname
    return React.cloneElement(li, modifiedProps, [text]);
  };

  render() {
    const { isOpen } = this.state;
    let {
      className = '',
      asField,
      ariaDescribedBy,
      data,
      dataCy = 'multiselect-wrap',
      dataItemKey,
      disabled,
      valid,
      value,
      label,
      omitItems = [],
      omitAllSelectedItems,
      errorTxt,
      size,
      labelClassName = '',
      withValidation,
      fieldClassName = '',
      fieldMandatory,
      fieldWithAdditionalEl,
      customDataCy
    } = this.props;

    const asFieldProps = {
      fieldClassName,
      fieldMandatory,
      fieldWithAdditionalEl
    };

    const multiselectOmittedProps = ['dataCy', 'required'].concat(ABSTRACT_FIELD_OMITTED_PROPS, FIELD_OMITTED_PROPS);
    const multiselectRestProps = _.omit(this.props, multiselectOmittedProps);
    const fieldWrapCls = classNames({
      [className]: className,
      'k-medium': size === 'medium',
      'k-state-empty': !value || value.length < 1,
      'k-state-invalid': valid === false,
      'k-state-disabled': disabled
    }
    );

    // Currently, behavior is to hide, not to show omitted items.
    // removes omitItems from multiselect picker if there are two or more that use the same data
    if (omitItems && omitItems.length) {
      data = data?.filter(item => _.isUndefined(dataItemKey) ? !omitItems.includes(item) : !omitItems.includes(item[dataItemKey]));
    }

    // Currently, behavior is to hide, not to show omitted items.
    // removes all selected items from multiselect picker if there are two or more that use the same data
    if (omitAllSelectedItems) {
      if (_.isUndefined(dataItemKey)) {
        data = data?.filter(item => !value.includes(item));
      } else {
        const values = _.cloneDeep(value).map(item => item[dataItemKey]);
        data = data?.filter(item => !values.includes(item[dataItemKey]));
      }
    }

    const content = (
      <div
        className={fieldWrapCls}
        data-cy={dataCy && customDataCy ? `${dataCy}-wrapper` : customDataCy ? undefined : dataCy}
      >
        {label && <Label
          editorId={this.multiselectID}
          className={classNames({
            [labelClassName]: labelClassName,
            'k-mandatory': !asField && fieldMandatory
          })}
        >
          {label}
        </Label>}
        <MultiSelect
          id={this.multiselectID}
          data-cy={dataCy ? dataCy : undefined}
          ref={(el: any) => this.multiselectRef = el}
          {...multiselectRestProps}
          itemRender={this.customComboboxItemRender}
          onClose={this.onClose}
          onOpen={this.onOpen}
          tagRender={this.customTagRender}
          onBlur={this.handleBlur}
          data={data}
          opened={isOpen}
        />
        {withValidation &&
        <div
          className={'tp-validation-error-message'}
					  // data-cy="error-message"
          data-cy={dataCy && customDataCy ? `${dataCy}-error-message` : customDataCy ? undefined : 'error-message'}
          id={ariaDescribedBy}
        >
          {errorTxt}
        </div>
        }
      </div>
    );

    if (asField) {
      return (
        <Field {...asFieldProps}>
          {content}
        </Field>
      );
    }

    return content;
  }

  /**
   * Helper method for triggering events outside the cmp itself
   * */
  triggerEvent(eventType: string, event: any) {
    if (this.props[eventType]) {
      this.props[eventType].call(undefined, {
        ...event,
        target: this.multiselectRef
      });
    }
  }
}

export default PuzzleMultiselect;
